import React, { useState } from 'react';
import { api, ApiError, ListeningSet } from '../../../config/api';
import { AdProcessingBanner } from '../../ads/Ads';

interface Props {
  // Stateless build: audio is never stored server-side, so the local File is
  // passed through for in-browser playback via an object URL.
  onUploaded: (set: ListeningSet, file: File) => void;
}

const ListeningUpload: React.FC<Props> = ({ onUploaded }) => {
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState<number>(0);
  const [progressMessage, setProgressMessage] = useState<string>('');
  const [selectedLanguage, setSelectedLanguage] = useState<string>('en-US');

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
      setError(null);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select an audio or video file.');
      return;
    }

    if (file.size / (1024 * 1024) > 25) {
      setError('File exceeds 25 MB limit.');
      return;
    }

    try {
      setLoading(true);
      setProgress(0);
      setProgressMessage('Uploading file...');

      const formData = new FormData();
      formData.append('file', file);
      
      // Use file name as title, truncate if too long (max 30 characters)
      const fileName = file.name;
      const fileExtension = fileName.split('.').pop();
      const fileNameWithoutExt = fileName.replace(`.${fileExtension}`, '');
      const title = fileNameWithoutExt.length > 30 
        ? `${fileNameWithoutExt.substring(0, 27)}...` 
        : fileNameWithoutExt;
      
      formData.append('title', title);
      
      // Add language codes to the request
      formData.append('language_codes', selectedLanguage);
      
      // The backend does upload → convert → transcribe → question generation
      // in ONE long request, so real per-stage progress isn't available.
      // Instead of hard-coded stage jumps (which sat at 15% for the whole
      // request, then sprinted to 100%), ease the bar smoothly toward 95%
      // with diminishing steps, and swap the phase label by elapsed time.
      const startTime = Date.now();
      const progressInterval = setInterval(() => {
        const elapsed = (Date.now() - startTime) / 1000;
        setProgress((prev) => Math.min(prev + (95 - prev) * 0.02, 95));
        if (elapsed < 4) setProgressMessage('Uploading file...');
        else if (elapsed < 12) setProgressMessage('Converting audio format...');
        else if (elapsed < 45) setProgressMessage('Transcribing audio...');
        else setProgressMessage('Generating questions... (almost there)');
      }, 200);

      let response;
      try {
        response = await api.generateListening(formData);
      } finally {
        clearInterval(progressInterval);
      }

      setProgress(100);
      setProgressMessage('Generation complete!');
      
      if (response.data) {
        onUploaded(response.data, file);
      }
    } catch (e: unknown) {
      if (e instanceof ApiError && e.status === 429) {
        setError("You've reached today's limit of 3 listening generations. It resets tomorrow — please come back then.");
      } else {
        const msg = e instanceof ApiError ? e.message : 'Upload failed';
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="listening-upload">
      <h3 className="section-title">1. Select audio or video</h3>
      <div className="upload-area">
        <p>Please upload an audio or video file. The file size should be less than 25MB.</p>
        <div className="file-upload-container">
          <input
            className="file-input"
            type="file"
            accept="audio/*,video/*"
            onChange={handleFileChange}
          />
        </div>
        {file && (
          <p className="file-info">
            Selected: <strong>{file.name}</strong> ({(file.size / (1024 * 1024)).toFixed(2)} MB)
          </p>
        )}
        {error && <p className="error-message">{error}</p>}
      </div>

      {/* Language Selection */}
      <div className="language-selection" style={{ marginTop: '24px' }}>
        <h3 className="section-title">2. Select language</h3>
        <p style={{ marginBottom: '12px', color: 'var(--text-secondary)', fontSize: '14px' }}>
          Choose the primary language of your audio/video file for better transcription accuracy.
        </p>
        <select
          className="form-select"
          value={selectedLanguage}
          onChange={(e) => setSelectedLanguage(e.target.value)}
          style={{ 
            width: '100%', 
            padding: '12px', 
            border: '1px solid var(--border-color)', 
            borderRadius: '8px',
            fontSize: '16px',
            backgroundColor: 'var(--bg-secondary)',
            color: 'var(--text-primary)'
          }}
        >
          <option value="en-US">English (US)</option>
          <option value="zh-CN">简体中文 (Simplified Chinese)</option>
          <option value="zh-TW">繁體中文 (Traditional Chinese - TW)</option>
          <option value="zh-HK">繁體中文 (Traditional Chinese - HK)</option>
          <option value="ja-JP">日本語 (Japanese)</option>
          <option value="ko-KR">한국어 (Korean)</option>
          <option value="es-ES">Español (Spanish)</option>
          <option value="fr-FR">Français (French)</option>
          <option value="de-DE">Deutsch (German)</option>
          <option value="it-IT">Italiano (Italian)</option>
          <option value="pt-BR">Português (Brazil)</option>
          <option value="ru-RU">Русский (Russian)</option>
          <option value="ar-SA">العربية (Arabic)</option>
          <option value="hi-IN">हिन्दी (Hindi)</option>
          <option value="th-TH">ไทย (Thai)</option>
          <option value="vi-VN">Tiếng Việt (Vietnamese)</option>
        </select>
      </div>

      <p style={{ marginTop: '16px', color: 'var(--text-light)', fontSize: '13px' }}>
        You can generate up to 3 listening practices per day.
      </p>

      {loading && (
        <>
          <AdProcessingBanner active={loading} />
          <div className="progress-container" style={{ marginTop: '24px' }}>
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${progress}%`, background: 'var(--primary-gradient)' }} />
            </div>
            <p className="progress-message">{progressMessage} ({Math.round(progress)}%)</p>
          </div>
        </>
      )}

      <div className="upload-actions" style={{ textAlign:'center', marginTop:'24px' }}>
        <button className="btn btn-primary process-button" onClick={handleUpload} disabled={loading || !file}>
          {loading ? 'Processing...' : 'Generate Questions'}
        </button>
      </div>
    </div>
  );
};

export default ListeningUpload; 